import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { updateTemplate } from '@/modules/templates/services/templates.service';
import { Eye, Link as LinkIcon, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';

interface PreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  templateSlug: string;
  locale: string;
  previewToken: string | null;
  onTokenGenerated: (token: string) => void;
}

export function PreviewDialog({
  open,
  onOpenChange,
  templateId,
  templateSlug,
  locale,
  previewToken,
  onTokenGenerated,
}: PreviewDialogProps) {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const { t } = useTranslation();

  const handleGenerateToken = async () => {
    setIsGenerating(true);
    try {
      // Generate a random preview token and save it to UDS
      const token = crypto.randomUUID().replace(/-/g, '').slice(0, 24);
      await updateTemplate(templateId, { PreviewToken: token });
      onTokenGenerated(token);
      toast({ variant: 'success', title: 'Success', description: t('TEMPLATEBUILDER_PREVIEWDIALOG_TOAST_GENERATED') });
    } catch (error) {
      console.error('Error generating preview token:', error);
      toast({ variant: 'destructive', title: 'Error', description: error instanceof Error
          ? error.message
          : t('TEMPLATEBUILDER_PREVIEWDIALOG_ERRORS_GENERATEFAILED') });
    } finally {
      setIsGenerating(false);
    }
  };

  const previewUrl = previewToken
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/templates/${templateSlug}/generate?preview=${previewToken}`
    : '';

  const handleCopyLink = async () => {
    if (!previewUrl) return;
    try {
      await navigator.clipboard.writeText(previewUrl);
      toast({ variant: 'success', title: 'Success', description: t('TEMPLATEBUILDER_PREVIEWDIALOG_TOAST_COPIED') });
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: t('TEMPLATEBUILDER_PREVIEWDIALOG_TOAST_COPYFAILED') });
    }
  };

  const handleOpenPreview = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-125">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-selise-blue" />
            <DialogTitle>{t('TEMPLATEBUILDER_PREVIEWDIALOG_TITLE')}</DialogTitle>
          </div>
          <DialogDescription>{t('TEMPLATEBUILDER_PREVIEWDIALOG_DESCRIPTION')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {previewToken ? (
            <>
              <div className="space-y-2">
                <Label>{t('TEMPLATEBUILDER_PREVIEWDIALOG_URLLABEL')}</Label>
                <div className="flex gap-2">
                  <Input readOnly value={previewUrl} className="font-mono text-sm" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopyLink}
                    title={t('TEMPLATEBUILDER_PREVIEWDIALOG_COPYTITLE')}
                  >
                    <LinkIcon className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-globe-grey">
                  {t('TEMPLATEBUILDER_PREVIEWDIALOG_URLHELP')}
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="button" onClick={handleOpenPreview} className="flex-1 gap-2">
                  <Eye className="h-4 w-4" />
                  {t('TEMPLATEBUILDER_PREVIEWDIALOG_OPENPREVIEW')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateToken}
                  disabled={isGenerating}
                  className="gap-2"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('TEMPLATEBUILDER_PREVIEWDIALOG_GENERATING')}
                    </>
                  ) : (
                    <>
                      <LinkIcon className="h-4 w-4" />
                      {t('TEMPLATEBUILDER_PREVIEWDIALOG_REGENERATE')}
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-selise-blue/5 border border-selise-blue/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <LinkIcon className="h-5 w-5 text-selise-blue mt-0.5" />
                  <div>
                    <p className="font-medium text-[hsl(var(--fg))]">
                      {t('TEMPLATEBUILDER_PREVIEWDIALOG_EMPTYTITLE')}
                    </p>
                    <p className="text-sm text-globe-grey mt-1">
                      {t('TEMPLATEBUILDER_PREVIEWDIALOG_EMPTYDESCRIPTION')}
                    </p>
                  </div>
                </div>
              </div>
              <Button
                type="button"
                onClick={handleGenerateToken}
                disabled={isGenerating}
                className="w-full gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('TEMPLATEBUILDER_PREVIEWDIALOG_GENERATING')}
                  </>
                ) : (
                  <>
                    <LinkIcon className="h-4 w-4" />
                    {t('TEMPLATEBUILDER_PREVIEWDIALOG_GENERATE')}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
